import { faChevronCircleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { connect, useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { useCheckoutScreenContext } from "../../hooks/useCheckoutScreenContext";
import GooglePayButtonContainerMobile from "../../mobileComponents/paymentTypes/mastercardDirectPayment/googlePayMobile";
import { findUnschedulableItems } from "../../helpers/categorySchedule";
import { promotionsOperations } from "../../state/features/promotions";
import { loaderOperations } from "../../state/features/loader";
import { errorOperations } from "../../state/features/error";
import { usePromotionsContext } from "../../hooks/usePromotionsContext";
import { getPromoCodeVerificationPayload } from "../../features/promotions/helpers/utils";

interface ICheckoutCtaButtonProps {
  placeOrderEnabled: boolean;
  outOfStockItems: any[];
  recentOrder: any;
  orderPayload: any;
  storeOpen: boolean;
  cartItems: any[];
  deliveryTime: Date;
  clickAction: () => void;
  backAction: () => void;
  showLoader: () => void;
  hideLoader: () => void;
  updateError: (error: any) => void;
  verifyPromoCode: (payload: any) => void;
}

const CheckoutCtaButton = ({
  placeOrderEnabled,
  outOfStockItems,
  orderPayload,
  recentOrder,
  storeOpen,
  cartItems,
  deliveryTime,
  clickAction,
  backAction,
  showLoader,
  hideLoader,
  updateError,
  verifyPromoCode,
}: ICheckoutCtaButtonProps) => {
  const { paymentTypes, menu } = useSelector((state: RootState) => state);
  const { allow_order_when_closed: allowOrderWhenClosed = false } = useSelector(
    (state: RootState) => state.company?.configuration || {}
  );
  const { availablePromotions = [] } = useSelector(
    (state: RootState) => state.promotions
  );
  const { mpgsAddonPaymentType, checkoutPageConfigs } =
    useCheckoutScreenContext();
  const { sapaad_company_id: sapaadCompanyId } = useSelector(
    (state: RootState) => state.company
  );
  const { sapaad_customer_id: sapaadCustomerId } = useSelector(
    (state: RootState) => state.user
  );
  const { appliedPromotionsData, appliedPromoCode, resetPromocode } =
    usePromotionsContext();

  const intl = useIntl();

  const unschedulableItems = useMemo(() => {
    if (allowOrderWhenClosed) {
      if (!deliveryTime) {
        return [...cartItems];
      }
      return findUnschedulableItems(
        deliveryTime,
        cartItems,
        menu.categories
      );
    } else {
      return [];
    }
  }, [
    deliveryTime,
    cartItems,
    menu.categories,
    allowOrderWhenClosed,
    storeOpen,
  ]);

  const checkPromoCodeValidity = async () => {
    if (appliedPromoCode === '') {
      return true;
    }
    showLoader();
    const promoVerificationPayload = getPromoCodeVerificationPayload(
      appliedPromoCode,
      appliedPromotionsData.availablePromoCodes,
      sapaadCompanyId,
      sapaadCustomerId
    );

    let isPromoCodeValid = false;
    try {
      const result = await verifyPromoCode(promoVerificationPayload) as any;
      isPromoCodeValid = result.payload?.data?.is_valid;
    } catch (error) {
      console.error('An unexpected error occurred:', error);
      isPromoCodeValid = false;
      resetPromocode();
    } finally {
      hideLoader();
    }
    return isPromoCodeValid;
  };

  const ctaButtonClickHandler = async () => {
    if (availablePromotions.length === 0) {
      // No promotions available, proceed with order placement
      clickAction();
      return;
    }
    const isPromoCodeValid = await checkPromoCodeValidity();
    if (!isPromoCodeValid) {
      updateError({
        title: intl.formatMessage({
          id: 'error.coupons_invalid',
          defaultMessage: 'Please enter a valid promo code',
        }),
        message: intl.formatMessage({
          id: 'error.coupons_invalid_message',
          defaultMessage:
            'This promo has reached its maximum usage. Please try another code.',
        }),
      });

      return;
    }
    clickAction();
  };

  const disableCtaButton: boolean = useMemo(() => {
    return (
      (!storeOpen && !allowOrderWhenClosed) ||
      !placeOrderEnabled ||
      !cartItems?.length ||
      outOfStockItems.length ||
      recentOrder ||
      !checkoutPageConfigs.showPaymentMethods ||
      unschedulableItems.length
    );
  }, [
    storeOpen,
    allowOrderWhenClosed,
    placeOrderEnabled,
    outOfStockItems,
    recentOrder,
    cartItems,
    checkoutPageConfigs.showPaymentMethods,
    unschedulableItems,
  ]);

  const isMastercardDirectPayment = useMemo(() => {
    const masterCardPaymentType = paymentTypes.find(
      (p: any) => p.payment_type === "mastercard"
    );
    if (orderPayload?.payment?.payment_type === "mastercard") {
      return masterCardPaymentType?.direct_payment_enabled && true;
    }
    return false;
  }, [orderPayload]);

  if (
    isMastercardDirectPayment &&
    !disableCtaButton &&
    mpgsAddonPaymentType !== "Card"
  ) {
    if (mpgsAddonPaymentType === "Google Pay") {
      return <GooglePayButtonContainerMobile orderPayload={orderPayload} />;
    } else if (mpgsAddonPaymentType === "Apple Pay") {
      return (
        <button
          className="apple-pay-button apple-pay-button-black w-100"
          style={{ height: "45px" }}
          onClick={clickAction}
        ></button>
      );
    } else {
      return null;
    }
  } else {
    return (
      <React.Fragment>
        <Link
          onClick={backAction}
          to="/m/order-details"
          className="btn button btn-outline-primary w-100 mr-4"
        >
          <FormattedMessage id="global.back" defaultMessage="Back" />
        </Link>
        <button
          id="checkoutNextButton"
          className="btn button btn-primary w-100"
          disabled={disableCtaButton}
          onClick={clickAction}
        >
          <FormattedMessage
            id="checkout.place_order"
            defaultMessage="Place order"
          />{" "}
          <FontAwesomeIcon className="ml-2" icon={faChevronCircleRight} />
        </button>
      </React.Fragment>
    );
  }
};

const mapDispatchToProps = {
  verifyPromoCode: promotionsOperations.reValidatePromoCode,
  showLoader: loaderOperations.showLoader,
  hideLoader: loaderOperations.hideLoader,
  updateError: errorOperations.updateError,
};
export default connect(null, mapDispatchToProps)(CheckoutCtaButton);
